if (!customElements.get('c2b-product-gallery')) {
  customElements.define(
    'c2b-product-gallery',
    class C2BProductGallery extends HTMLElement {
      constructor() {
        super();

        this.abortController = null;
        this.mql = window.matchMedia('(min-width: 990px)');
        this.activeMediaId = null;
        this.lightboxNode = null;
        this.lightboxItems = [];
        this.lightboxIndex = 0;
        this.lightboxTrigger = null;
        this.preloadedLightboxSources = new Set();
        this.lightboxImageLoadToken = 0;
        this.lightboxImageLoading = false;
        this.lightboxImageLoadTimeout = null;
        this.lightboxAdjacentPreloadHandle = null;
        this.lightboxAdjacentPreloadMode = null;
        this.lightboxZoom = {
          x: 0,
          y: 0,
          startX: 0,
          startY: 0,
          originX: 0,
          originY: 0,
          isPanning: false,
          pointerId: null,
        };
        this.lightboxMouseMovePanFrame = null;
        this.lightboxMouseMovePanTarget = { x: 0, y: 0 };
        this.lightboxSwipe = {
          startX: 0,
          startY: 0,
          lastX: 0,
          lastY: 0,
          isTracking: false,
          pointerId: null,
        };

        this.onLightboxKeydown = this.onLightboxKeydown.bind(this);
        this.onLightboxFullscreenChange = this.onLightboxFullscreenChange.bind(this);
      }

      connectedCallback() {
        this.initializeGallery();
      }

      initializeGallery() {
        this.abortController?.abort();
        this.abortController = new AbortController();

        this.cacheElements();
        if (!this.elements.viewer) return false;

        this.bindThumbnailClicks();
        this.bindViewerClicks();
        this.bindNativeModalOpenerGuards();
        this.bindThumbnailNavigation();
        this.bindThumbnailImageLoadUpdates();
        this.bindViewerSlideChanges();

        this.ensureActiveMedia();
        this.initializeImageLoadingStates();
        this.updateThumbnailNavigationState();
        this.scheduleThumbnailNavigationStateUpdate();

        return true;
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.closeLightbox({ restoreFocus: false });
      }

      get signal() {
        return this.abortController?.signal;
      }

      cacheElements() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          viewerList: this.querySelector('[id^="Slider-Gallery"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          thumbnailList: this.querySelector('[id^="Slider-Thumbnails"]'),
        };
      }

      safeSelectorValue(value) {
        if (window.CSS && typeof window.CSS.escape === 'function') {
          return window.CSS.escape(value);
        }

        return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      }

      isInsideQuickAddModal() {
        return Boolean(this.closest('quick-add-modal'));
      }

      isInsideQuickViewModal() {
        return Boolean(this.closest('quick-add-modal.c2b-quick-view-modal'));
      }

      usesVerticalThumbnails() {
        return (
          this.mql.matches &&
          Boolean(this.elements.thumbnailList) &&
          (!this.isInsideQuickAddModal() || this.isInsideQuickViewModal())
        );
      }

      scheduleThumbnailNavigationStateUpdate() {
        const runUpdate = () => {
          if (!this.isConnected) return;

          this.cacheElements();
          this.updateThumbnailNavigationState();
        };

        window.requestAnimationFrame(() => {
          runUpdate();
          window.requestAnimationFrame(runUpdate);
        });

        [80, 180, 360, 720].forEach((delay) => {
          window.setTimeout(runUpdate, delay);
        });
      }

      bindThumbnailImageLoadUpdates() {
        if (!this.elements.thumbnailList) return;

        this.elements.thumbnailList.querySelectorAll('img').forEach((image) => {
          if (image.complete) return;

          image.addEventListener('load', () => this.scheduleThumbnailNavigationStateUpdate(), {
            once: true,
            signal: this.signal,
          });
          image.addEventListener('error', () => this.scheduleThumbnailNavigationStateUpdate(), {
            once: true,
            signal: this.signal,
          });
        });
      }

      bindThumbnailClicks() {
        this.addEventListener(
          'click',
          (event) => {
            const target = event.target instanceof Element ? event.target : null;
            if (!target) return;

            const thumbnailItem = target.closest('[id^="GalleryThumbnails"] [data-target]');
            if (!thumbnailItem || !this.contains(thumbnailItem)) return;

            const mediaId = thumbnailItem.dataset.target;
            if (!mediaId) return;

            event.preventDefault();
            event.stopPropagation();

            this.setActiveMedia(mediaId, false);
          },
          { signal: this.signal, capture: true }
        );
      }

      bindViewerClicks() {
        this.addEventListener(
          'click',
          (event) => {
            if (this.isInsideQuickAddModal()) return;

            const target = event.target instanceof Element ? event.target : null;
            if (!target) return;

            const mediaToggle = target.closest('.product__media-toggle, .product__modal-opener--image');
            if (!mediaToggle || !this.contains(mediaToggle)) return;
            if (mediaToggle.closest('[id^="GalleryThumbnails"]')) return;

            const mediaItem = mediaToggle.closest('[data-media-id]') || this.getActiveMedia();
            if (!mediaItem) return;

            const image = mediaItem.querySelector('.product__modal-opener--image img, .product__media img');
            if (!image) return;

            event.preventDefault();
            event.stopImmediatePropagation();

            this.openLightbox(mediaItem.dataset.mediaId, mediaToggle);
          },
          { capture: true, signal: this.signal }
        );
      }

      bindNativeModalOpenerGuards() {
        if (this.isInsideQuickAddModal()) return;

        this.querySelectorAll('modal-opener.product__modal-opener, modal-opener[data-modal^="#ProductModal"]').forEach((opener) => {
          opener.addEventListener(
            'click',
            (event) => {
              const eventTarget = event.target instanceof Element ? event.target : opener;
              const mediaItem = opener.closest('[data-media-id]') || eventTarget.closest('[data-media-id]') || this.getActiveMedia();
              const image = mediaItem?.querySelector('.product__modal-opener--image img, .product__media img');

              if (!mediaItem || !image) return;

              event.preventDefault();
              event.stopImmediatePropagation();
              event.stopPropagation();

              this.openLightbox(mediaItem.dataset.mediaId, eventTarget.closest('button, modal-opener') || opener);
            },
            { capture: true, signal: this.signal }
          );
        });
      }

      bindThumbnailNavigation() {
        if (!this.elements.thumbnails || !this.elements.thumbnailList) return;

        const previousButton = this.elements.thumbnails.querySelector('.slider-button--prev');
        const nextButton = this.elements.thumbnails.querySelector('.slider-button--next');

        if (!previousButton || !nextButton) return;

        previousButton.addEventListener(
          'click',
          (event) => {
            if (!this.usesVerticalThumbnails()) return;

            event.preventDefault();
            event.stopImmediatePropagation();

            this.scrollThumbnailRail(-1);
          },
          { capture: true, signal: this.signal }
        );

        nextButton.addEventListener(
          'click',
          (event) => {
            if (!this.usesVerticalThumbnails()) return;

            event.preventDefault();
            event.stopImmediatePropagation();

            this.scrollThumbnailRail(1);
          },
          { capture: true, signal: this.signal }
        );

        this.elements.thumbnailList.addEventListener('scroll', () => this.updateThumbnailNavigationState(), {
          passive: true,
          signal: this.signal,
        });
        window.addEventListener('resize', () => this.updateThumbnailNavigationState(), { signal: this.signal });
      }

      bindViewerSlideChanges() {
        this.elements.viewer.addEventListener(
          'slideChanged',
          (event) => {
            const currentElement = event.detail?.currentElement;
            const mediaId = currentElement?.dataset?.mediaId;
            if (!mediaId) return;

            this.setActiveThumbnailByMediaId(mediaId);
          },
          { signal: this.signal }
        );
      }

      ensureActiveMedia() {
        const activeMedia = this.getActiveMedia() || this.elements.viewer?.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.activeMediaId = activeMedia.dataset.mediaId;
        activeMedia.classList.add('is-active');
        this.setActiveThumbnailByMediaId(this.activeMediaId);
      }

      getActiveMedia() {
        return (
          this.elements.viewer?.querySelector('[data-media-id].is-active') ||
          this.elements.viewer?.querySelector('[data-media-id]')
        );
      }

      getThumbnailForMediaId(mediaId) {
        if (!this.elements.thumbnails || !mediaId) return null;

        return this.elements.thumbnails.querySelector(`[data-target="${this.safeSelectorValue(mediaId)}"]`);
      }

      setActiveMedia(mediaId, prepend = false) {
        this.cacheElements();

        const selectorMediaId = this.safeSelectorValue(mediaId);
        const activeMedia =
          this.elements.viewer?.querySelector(`[data-media-id="${selectorMediaId}"]`) ||
          this.elements.viewer?.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => {
          item.classList.remove('is-active');
          this.clearMediaLoadingState(item);
        });

        activeMedia.classList.add('is-active');
        this.activeMediaId = activeMedia.dataset.mediaId;

        if (prepend && activeMedia.parentElement && activeMedia.parentElement.firstElementChild !== activeMedia) {
          activeMedia.parentElement.prepend(activeMedia);

          const activeThumbnail = this.getThumbnailForMediaId(activeMedia.dataset.mediaId);
          if (activeThumbnail?.parentElement && activeThumbnail.parentElement.firstElementChild !== activeThumbnail) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          this.elements.viewer?.resetPages?.();
        }

        this.updateMediaLoadingState(activeMedia);
        this.preventStickyHeader();
        this.scrollActiveMediaIntoView(activeMedia);
        this.playActiveMedia(activeMedia);
        this.setActiveThumbnailByMediaId(activeMedia.dataset.mediaId);
        this.announceLiveRegion(activeMedia);
      }

      scrollActiveMediaIntoView(activeMedia) {
        const activeMediaParent = activeMedia.parentElement;
        if (!activeMediaParent) return;

        if (this.isInsideQuickAddModal() || !this.mql.matches) {
          activeMediaParent.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          return;
        }

        const activeMediaRect = activeMedia.getBoundingClientRect();
        if (activeMediaRect.top > -0.5) return;

        const top = activeMediaRect.top + window.scrollY;
        window.scrollTo({ top, behavior: 'smooth' });
      }

      setActiveThumbnailByMediaId(mediaId) {
        const thumbnail = this.getThumbnailForMediaId(mediaId);
        if (!thumbnail) return;

        this.setActiveThumbnail(thumbnail);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((button) => button.removeAttribute('aria-current'));

        const thumbnailButton = thumbnail.querySelector('button');
        if (thumbnailButton) thumbnailButton.setAttribute('aria-current', 'true');

        this.scrollThumbnailIntoView(thumbnail);
        this.updateThumbnailNavigationState();
      }

      scrollThumbnailIntoView(thumbnail) {
        if (!this.elements.thumbnailList) return;

        const list = this.elements.thumbnailList;

        if (this.usesVerticalThumbnails()) {
          const listRect = list.getBoundingClientRect();
          const thumbnailRect = thumbnail.getBoundingClientRect();
          const visible = thumbnailRect.top >= listRect.top + 4 && thumbnailRect.bottom <= listRect.bottom - 4;

          if (!visible) {
            list.scrollTo({
              top: thumbnail.offsetTop - list.offsetTop,
              behavior: 'smooth',
            });
          }

          return;
        }

        if (typeof list.isSlideVisible === 'function' && list.isSlideVisible(thumbnail, 10)) return;
        list.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }

      scrollThumbnailRail(direction) {
        if (!this.elements.thumbnailList) return;

        const firstThumbnail = this.elements.thumbnailList.querySelector('.thumbnail-list__item');
        const styles = window.getComputedStyle(this.elements.thumbnailList);
        const gap = parseFloat(styles.rowGap || styles.gap || '0') || 0;
        const itemHeight = firstThumbnail?.getBoundingClientRect().height || 0;
        const step = itemHeight > 0 ? (itemHeight + gap) * 3 : this.elements.thumbnailList.clientHeight * 0.75;

        this.elements.thumbnailList.scrollBy({
          top: direction * step,
          behavior: 'smooth',
        });

        window.setTimeout(() => this.updateThumbnailNavigationState(), 220);
      }

      updateThumbnailNavigationState() {
        if (!this.elements.thumbnails || !this.elements.thumbnailList) return;

        const previousButton = this.elements.thumbnails.querySelector('.slider-button--prev');
        const nextButton = this.elements.thumbnails.querySelector('.slider-button--next');

        if (!previousButton || !nextButton) return;
        if (!this.usesVerticalThumbnails()) {
          previousButton.disabled = false;
          nextButton.disabled = false;
          return;
        }

        const thumbnailList = this.elements.thumbnailList;
        const maxScrollTop = Math.max(0, thumbnailList.scrollHeight - thumbnailList.clientHeight);

        if (thumbnailList.clientHeight <= 0) {
          previousButton.disabled = true;
          nextButton.disabled = false;
          this.scheduleThumbnailNavigationStateUpdate();
          return;
        }

        previousButton.disabled = thumbnailList.scrollTop <= 1;
        nextButton.disabled = maxScrollTop <= 1 || thumbnailList.scrollTop >= maxScrollTop - 1;
      }

      announceLiveRegion(activeMedia) {
        if (!this.elements.liveRegion) return;

        const activeThumbnail = this.getThumbnailForMediaId(activeMedia.dataset.mediaId);
        const position = activeThumbnail?.dataset?.mediaPosition;
        if (!position || !window.accessibilityStrings?.imageAvailable) return;

        this.elements.liveRegion.setAttribute('aria-hidden', 'false');
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);

        window.setTimeout(() => {
          this.elements.liveRegion?.setAttribute('aria-hidden', 'true');
        }, 2000);
      }

      playActiveMedia(activeMedia) {
        window.pauseAllMedia?.();

        const deferredMedia = activeMedia.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      initializeImageLoadingStates() {
        const activeMedia = this.getActiveMedia();

        this.querySelectorAll('[id^="Slider-Gallery"] [data-media-id]').forEach((mediaItem) => {
          if (mediaItem === activeMedia) {
            this.updateMediaLoadingState(mediaItem);
            return;
          }

          this.clearMediaLoadingState(mediaItem);
        });
      }

      getMediaImage(mediaItem) {
        return mediaItem?.querySelector('.product__modal-opener--image img, .product__media img') || null;
      }

      clearMediaLoadingState(mediaItem) {
        const image = this.getMediaImage(mediaItem);
        if (!mediaItem || !image) return;

        delete mediaItem.dataset.c2bMediaLoadToken;
        delete image.dataset.c2bMediaLoadToken;

        mediaItem.classList.remove('c2b-media-loading');
        mediaItem.classList.add('c2b-media-loaded');

        image.classList.remove('c2b-image-loading');
        image.classList.add('c2b-image-loaded');
      }

      updateMediaLoadingState(mediaItem) {
        const image = this.getMediaImage(mediaItem);
        if (!mediaItem || !image) return;

        const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

        mediaItem.dataset.c2bMediaLoadToken = token;
        image.dataset.c2bMediaLoadToken = token;

        const markLoaded = () => {
          if (mediaItem.dataset.c2bMediaLoadToken !== token) return;
          if (image.dataset.c2bMediaLoadToken !== token) return;

          this.clearMediaLoadingState(mediaItem);
        };

        mediaItem.classList.remove('c2b-media-loaded');
        mediaItem.classList.add('c2b-media-loading');

        image.classList.remove('c2b-image-loaded');
        image.classList.add('c2b-image-loading');

        if (image.complete) {
          markLoaded();
          return;
        }

        image.addEventListener('load', markLoaded, { once: true, signal: this.signal });
        image.addEventListener('error', markLoaded, { once: true, signal: this.signal });

        if (image.decode) {
          image.decode().then(markLoaded).catch(markLoaded);
        }

        window.requestAnimationFrame(() => {
          if (image.complete) {
            markLoaded();
          }
        });

        window.setTimeout(markLoaded, 6000);
      }

      getImageSourceCandidates(image) {
        const srcset = image.getAttribute('srcset');
        if (!srcset) return [];

        return srcset
          .split(',')
          .map((candidate) => {
            const parts = candidate.trim().split(/\s+/);
            const url = parts[0];
            const width = parts[1]?.endsWith('w') ? Number.parseInt(parts[1], 10) : 0;
            return { url, width };
          })
          .filter((candidate) => candidate.url && candidate.width > 0)
          .sort((a, b) => a.width - b.width);
      }

      getBestImageSource(image) {
        const candidates = this.getImageSourceCandidates(image);
        if (!candidates.length) return image.currentSrc || image.src;

        return candidates[candidates.length - 1].url;
      }

      getImageSourceForWidth(image, targetWidth) {
        const candidates = this.getImageSourceCandidates(image);
        if (!candidates.length) return image.currentSrc || image.src;

        return (
          candidates.find((candidate) => candidate.width >= targetWidth)?.url ||
          candidates[candidates.length - 1].url
        );
      }

      collectLightboxItems() {
        return Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || [])
          .map((mediaItem) => {
            const image = mediaItem.querySelector('.product__modal-opener--image img, .product__media img');
            if (!image) return null;

            const thumbnail = this.getThumbnailForMediaId(mediaItem.dataset.mediaId);
            const thumbnailImage = thumbnail?.querySelector('img');

            return {
              mediaId: mediaItem.dataset.mediaId,
              src: this.getBestImageSource(image),
              thumbSrc: thumbnailImage
                ? this.getImageSourceForWidth(thumbnailImage, 208)
                : this.getImageSourceForWidth(image, 208),
              alt: image.alt || '',
            };
          })
          .filter(Boolean);
      }

      openLightbox(mediaId, trigger) {
        this.lightboxItems = this.collectLightboxItems();
        this.preloadedLightboxSources = new Set();
        if (!this.lightboxItems.length) return;

        const requestedIndex = this.lightboxItems.findIndex((item) => item.mediaId === mediaId);
        this.lightboxIndex = requestedIndex >= 0 ? requestedIndex : 0;
        this.lightboxTrigger = trigger || document.activeElement;

        if (!this.lightboxNode) this.createLightbox();

        document.documentElement.classList.add('c2b-product-lightbox-open');
        this.lightboxNode.hidden = false;
        this.lightboxNode.setAttribute('aria-hidden', 'false');

        this.renderLightboxImage();
        document.addEventListener('keydown', this.onLightboxKeydown);
        document.addEventListener('fullscreenchange', this.onLightboxFullscreenChange);
        document.addEventListener('webkitfullscreenchange', this.onLightboxFullscreenChange);
        this.updateFullscreenButton();
        this.lightboxNode.querySelector('.c2b-product-lightbox__close')?.focus();
      }

      createLightbox() {
        const lightbox = document.createElement('div');
        lightbox.className = 'c2b-product-lightbox';
        lightbox.hidden = true;
        lightbox.setAttribute('role', 'dialog');
        lightbox.setAttribute('aria-modal', 'true');
        lightbox.setAttribute('aria-hidden', 'true');
        lightbox.innerHTML = `
          <div class="c2b-product-lightbox__backdrop" data-c2b-lightbox-close></div>
          <div class="c2b-product-lightbox__dialog" role="document">
            <button type="button" class="c2b-product-lightbox__close" aria-label="Închide galeria" data-c2b-lightbox-close>
              <span aria-hidden="true">×</span>
            </button>
            <button type="button" class="c2b-product-lightbox__fullscreen" aria-label="Deschide pe tot ecranul" data-c2b-lightbox-fullscreen>
              <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" class="c2b-product-lightbox__fullscreen-icon">
                <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <button type="button" class="c2b-product-lightbox__zoom" aria-label="Mărește imaginea" data-c2b-lightbox-zoom>
              <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" class="c2b-product-lightbox__zoom-icon">
                <path d="M10.8 18.1a7.3 7.3 0 1 1 0-14.6 7.3 7.3 0 0 1 0 14.6Z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="m16.2 16.2 4.3 4.3M10.8 7.7v6.2M7.7 10.8h6.2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <button type="button" class="c2b-product-lightbox__nav c2b-product-lightbox__nav--prev" aria-label="Imaginea precedentă" data-c2b-lightbox-prev>
              <span aria-hidden="true">‹</span>
            </button>
            <figure class="c2b-product-lightbox__figure">
              <span class="c2b-product-lightbox__spinner" aria-hidden="true"></span>
              <img class="c2b-product-lightbox__image" alt="">
              <figcaption class="c2b-product-lightbox__counter"></figcaption>
            </figure>
            <button type="button" class="c2b-product-lightbox__nav c2b-product-lightbox__nav--next" aria-label="Imaginea următoare" data-c2b-lightbox-next>
              <span aria-hidden="true">›</span>
            </button>
          </div>
          <div class="c2b-product-lightbox__thumb-strip" data-c2b-lightbox-thumb-strip aria-label="Miniaturi galerie"></div>
        `;

        lightbox.addEventListener('click', (event) => {
          const target = event.target instanceof Element ? event.target : null;
          if (!target) return;

          const lightboxThumbnail = target.closest('[data-c2b-lightbox-thumb]');
          if (lightboxThumbnail) {
            const index = Number(lightboxThumbnail.dataset.c2bLightboxThumb);
            if (Number.isInteger(index)) this.showLightboxImage(index);
            return;
          }

          if (target.closest('[data-c2b-lightbox-close]')) {
            this.closeLightbox();
            return;
          }

          if (target.closest('[data-c2b-lightbox-prev]')) {
            this.showPreviousLightboxImage();
            return;
          }

          if (target.closest('[data-c2b-lightbox-next]')) {
            this.showNextLightboxImage();
            return;
          }

          if (target.closest('[data-c2b-lightbox-fullscreen]')) {
            this.toggleLightboxFullscreen();
            return;
          }

          if (target.closest('[data-c2b-lightbox-zoom]')) {
            this.toggleLightboxZoom();
          }
        });

        this.bindLightboxSwipe(lightbox);

        document.body.appendChild(lightbox);
        this.lightboxNode = lightbox;
      }

      setLightboxNavigationDisabled(disabled) {
        if (!this.lightboxNode) return;

        this.lightboxNode
          .querySelectorAll(
            '[data-c2b-lightbox-prev], [data-c2b-lightbox-next], [data-c2b-lightbox-thumb], [data-c2b-lightbox-zoom]'
          )
          .forEach((control) => {
            control.disabled = disabled;
            control.setAttribute('aria-disabled', disabled ? 'true' : 'false');
          });
      }

      setLightboxImageLoading(isLoading) {
        this.lightboxImageLoading = isLoading;

        if (!this.lightboxNode) return;

        this.lightboxNode.classList.toggle('c2b-product-lightbox--image-loading', isLoading);
        this.setLightboxNavigationDisabled(isLoading);

        if (!isLoading && this.lightboxImageLoadTimeout) {
          window.clearTimeout(this.lightboxImageLoadTimeout);
          this.lightboxImageLoadTimeout = null;
        }
      }

      renderLightboxImage() {
        if (!this.lightboxNode || !this.lightboxItems.length) return;

        this.cancelAdjacentLightboxPreload();
        this.disableLightboxZoom();

        const item = this.lightboxItems[this.lightboxIndex];
        const image = this.lightboxNode.querySelector('.c2b-product-lightbox__image');
        const counter = this.lightboxNode.querySelector('.c2b-product-lightbox__counter');
        const previousButton = this.lightboxNode.querySelector('[data-c2b-lightbox-prev]');
        const nextButton = this.lightboxNode.querySelector('[data-c2b-lightbox-next]');

        if (!image || !item?.src) {
          this.setLightboxImageLoading(false);
          return;
        }

        if (counter) counter.textContent = `${this.lightboxIndex + 1} / ${this.lightboxItems.length}`;

        this.renderLightboxThumbnails();

        const hasMultipleImages = this.lightboxItems.length > 1;
        if (previousButton) previousButton.hidden = !hasMultipleImages;
        if (nextButton) nextButton.hidden = !hasMultipleImages;

        const loadToken = this.lightboxImageLoadToken + 1;
        this.lightboxImageLoadToken = loadToken;

        let isResolved = false;

        const markLoaded = () => {
          if (isResolved || loadToken !== this.lightboxImageLoadToken) return;

          isResolved = true;

          image.removeEventListener('load', markLoaded);
          image.removeEventListener('error', markLoaded);

          image.classList.remove('c2b-image-loading');
          image.classList.add('c2b-image-loaded');

          this.setLightboxImageLoading(false);

          if (image.complete && image.naturalWidth > 0) {
            this.preloadAdjacentLightboxImages();
          }
        };

        image.removeEventListener('load', markLoaded);
        image.removeEventListener('error', markLoaded);

        this.setLightboxImageLoading(true);

        image.classList.remove('c2b-image-loaded');
        image.classList.add('c2b-image-loading');

        image.addEventListener('load', markLoaded, { once: true });
        image.addEventListener('error', markLoaded, { once: true });

        image.alt = item.alt || '';
        image.src = item.src;

        if (image.decode) {
          image.decode().then(markLoaded).catch(markLoaded);
        }

        window.requestAnimationFrame(() => {
          if (image.complete && image.naturalWidth > 0) {
            markLoaded();
          }
        });

        this.lightboxImageLoadTimeout = window.setTimeout(markLoaded, 6000);
      }

      preloadLightboxImage(index) {
        if (!this.lightboxItems.length) return;

        const item = this.lightboxItems[index];
        if (!item?.src || this.preloadedLightboxSources.has(item.src)) return;

        this.preloadedLightboxSources.add(item.src);

        const image = new Image();
        image.decoding = 'async';
        image.src = item.src;
      }

      cancelAdjacentLightboxPreload() {
        if (this.lightboxAdjacentPreloadHandle === null) return;

        if (this.lightboxAdjacentPreloadMode === 'idle' && typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(this.lightboxAdjacentPreloadHandle);
        } else {
          window.clearTimeout(this.lightboxAdjacentPreloadHandle);
        }

        this.lightboxAdjacentPreloadHandle = null;
        this.lightboxAdjacentPreloadMode = null;
      }

      preloadAdjacentLightboxImages() {
        if (this.lightboxItems.length < 2) return;

        this.cancelAdjacentLightboxPreload();

        const sourceIndex = this.lightboxIndex;
        const preloadNextImage = () => {
          this.lightboxAdjacentPreloadHandle = null;
          this.lightboxAdjacentPreloadMode = null;

          if (!this.lightboxNode || this.lightboxNode.hidden) return;
          if (this.lightboxIndex !== sourceIndex) return;

          const nextIndex = (sourceIndex + 1) % this.lightboxItems.length;
          this.preloadLightboxImage(nextIndex);
        };

        if (typeof window.requestIdleCallback === 'function') {
          this.lightboxAdjacentPreloadMode = 'idle';
          this.lightboxAdjacentPreloadHandle = window.requestIdleCallback(preloadNextImage, { timeout: 1500 });
          return;
        }

        this.lightboxAdjacentPreloadMode = 'timeout';
        this.lightboxAdjacentPreloadHandle = window.setTimeout(preloadNextImage, 500);
      }

      showLightboxImage(index) {
        if (this.lightboxImageLoading) return;
        if (!this.lightboxItems.length) return;
        if (index < 0 || index >= this.lightboxItems.length) return;
        if (index === this.lightboxIndex) return;

        this.lightboxIndex = index;
        this.renderLightboxImage();
      }

      renderLightboxThumbnails() {
        const strip = this.lightboxNode?.querySelector('[data-c2b-lightbox-thumb-strip]');
        if (!strip) return;

        const hasMultipleImages = this.lightboxItems.length > 1;
        strip.hidden = !hasMultipleImages;
        strip.setAttribute('aria-hidden', hasMultipleImages ? 'false' : 'true');

        if (!hasMultipleImages) {
          strip.innerHTML = '';
          strip.dataset.renderedCount = '0';
          return;
        }

        if (strip.dataset.renderedCount !== String(this.lightboxItems.length)) {
          strip.innerHTML = this.lightboxItems
            .map((item, index) => {
              const alt = item.alt ? item.alt.replace(/"/g, '&quot;') : `Imagine ${index + 1}`;
              return `
                <button type="button" class="c2b-product-lightbox__thumb" data-c2b-lightbox-thumb="${index}" aria-label="Deschide imaginea ${index + 1}">
                  <img src="${item.thumbSrc}" alt="${alt}" loading="lazy">
                </button>
              `;
            })
            .join('');
          strip.dataset.renderedCount = String(this.lightboxItems.length);
        }

        strip.querySelectorAll('[data-c2b-lightbox-thumb]').forEach((button, index) => {
          const isActive = index === this.lightboxIndex;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-current', isActive ? 'true' : 'false');

          if (isActive) {
            window.setTimeout(() => {
              button.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
            }, 0);
          }
        });
      }

      getLightboxZoomScale() {
        const rawScale = window
          .getComputedStyle(this.lightboxNode || document.documentElement)
          .getPropertyValue('--c2b-lightbox-zoom-scale');
        const parsedScale = Number.parseFloat(rawScale);

        return Number.isFinite(parsedScale) && parsedScale > 1 ? parsedScale : 2;
      }

      onLightboxMouseMovePan(event) {
        if (!this.lightboxNode || this.lightboxNode.hidden) return;
        if (!this.isLightboxZoomed()) return;
        if (this.lightboxZoom.isPanning) return;
        if (event.buttons !== 0) return;

        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest('button, [data-c2b-lightbox-thumb-strip]')) return;

        const figure = event.currentTarget instanceof Element ? event.currentTarget : null;
        if (!figure) return;

        const rect = figure.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return;

        const normalizedX = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
        const normalizedY = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
        const scale = this.getLightboxZoomScale();

        const maxX = (rect.width * (scale - 1)) / (2 * scale);
        const maxY = (rect.height * (scale - 1)) / (2 * scale);

        this.scheduleLightboxMouseMovePan({
          x: (0.5 - normalizedX) * maxX * 2,
          y: (0.5 - normalizedY) * maxY * 2,
        });
      }

      scheduleLightboxMouseMovePan(position) {
        this.lightboxMouseMovePanTarget = position;

        if (this.lightboxMouseMovePanFrame) return;

        this.lightboxMouseMovePanFrame = window.requestAnimationFrame(() => {
          this.lightboxMouseMovePanFrame = null;

          if (!this.lightboxNode || !this.isLightboxZoomed() || this.lightboxZoom.isPanning) return;

          this.lightboxZoom.x = this.lightboxMouseMovePanTarget.x;
          this.lightboxZoom.y = this.lightboxMouseMovePanTarget.y;
          this.applyLightboxZoomTransform();
        });
      }

      cancelLightboxMouseMovePan() {
        if (this.lightboxMouseMovePanFrame) {
          window.cancelAnimationFrame(this.lightboxMouseMovePanFrame);
        }

        this.lightboxMouseMovePanFrame = null;
        this.lightboxMouseMovePanTarget = { x: 0, y: 0 };
      }

      bindLightboxSwipe(lightbox) {
        const figure = lightbox.querySelector('.c2b-product-lightbox__figure');
        if (!figure) return;

        figure.addEventListener('pointerdown', (event) => this.onLightboxSwipeStart(event));
        figure.addEventListener('pointermove', (event) => this.onLightboxSwipeMove(event));
        figure.addEventListener('pointerup', (event) => this.onLightboxSwipeEnd(event));
        figure.addEventListener('pointercancel', () => this.resetLightboxSwipe());
        figure.addEventListener('mousemove', (event) => this.onLightboxMouseMovePan(event));
      }

      onLightboxSwipeStart(event) {
        if (!this.lightboxNode || this.lightboxNode.hidden) return;
        if (this.lightboxImageLoading) return;

        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest('button')) return;

        const isZoomed = this.isLightboxZoomed();

        if (event.pointerType === 'mouse' && !isZoomed) return;

        if (isZoomed) {
          this.lightboxZoom.startX = event.clientX;
          this.lightboxZoom.startY = event.clientY;
          this.lightboxZoom.originX = this.lightboxZoom.x;
          this.lightboxZoom.originY = this.lightboxZoom.y;
          this.lightboxZoom.isPanning = true;
          this.lightboxZoom.pointerId = event.pointerId;
          this.lightboxNode.classList.add('is-panning');
          event.currentTarget?.setPointerCapture?.(event.pointerId);
          event.preventDefault();
          return;
        }

        if (this.lightboxItems.length < 2) return;

        this.lightboxSwipe = {
          startX: event.clientX,
          startY: event.clientY,
          lastX: event.clientX,
          lastY: event.clientY,
          isTracking: true,
          pointerId: event.pointerId,
        };

        event.currentTarget?.setPointerCapture?.(event.pointerId);
      }

      onLightboxSwipeMove(event) {
        if (this.lightboxZoom.isPanning && this.lightboxZoom.pointerId === event.pointerId) {
          this.lightboxZoom.x = this.lightboxZoom.originX + event.clientX - this.lightboxZoom.startX;
          this.lightboxZoom.y = this.lightboxZoom.originY + event.clientY - this.lightboxZoom.startY;
          this.applyLightboxZoomTransform();
          event.preventDefault();
          return;
        }

        if (!this.lightboxSwipe.isTracking || this.lightboxSwipe.pointerId !== event.pointerId) return;

        const deltaX = event.clientX - this.lightboxSwipe.startX;
        const deltaY = event.clientY - this.lightboxSwipe.startY;

        this.lightboxSwipe.lastX = event.clientX;
        this.lightboxSwipe.lastY = event.clientY;

        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 12) {
          event.preventDefault();
        }
      }

      onLightboxSwipeEnd(event) {
        if (this.lightboxZoom.isPanning && this.lightboxZoom.pointerId === event.pointerId) {
          this.lightboxZoom.isPanning = false;
          this.lightboxZoom.pointerId = null;
          this.lightboxNode?.classList.remove('is-panning');
          event.currentTarget?.releasePointerCapture?.(event.pointerId);
          event.preventDefault();
          return;
        }

        if (!this.lightboxSwipe.isTracking || this.lightboxSwipe.pointerId !== event.pointerId) return;

        const deltaX = this.lightboxSwipe.lastX - this.lightboxSwipe.startX;
        const deltaY = this.lightboxSwipe.lastY - this.lightboxSwipe.startY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        const shouldSwipe = absX >= 48 && absX > absY * 1.35;

        event.currentTarget?.releasePointerCapture?.(event.pointerId);
        this.resetLightboxSwipe();

        if (!shouldSwipe) return;

        event.preventDefault();
        event.stopPropagation();

        if (deltaX < 0) {
          this.showNextLightboxImage();
        } else {
          this.showPreviousLightboxImage();
        }
      }

      resetLightboxSwipe() {
        this.lightboxSwipe = {
          startX: 0,
          startY: 0,
          lastX: 0,
          lastY: 0,
          isTracking: false,
          pointerId: null,
        };
      }

      showPreviousLightboxImage() {
        if (this.lightboxImageLoading) return;
        if (!this.lightboxItems.length) return;

        this.lightboxIndex = (this.lightboxIndex - 1 + this.lightboxItems.length) % this.lightboxItems.length;
        this.renderLightboxImage();
      }

      showNextLightboxImage() {
        if (this.lightboxImageLoading) return;
        if (!this.lightboxItems.length) return;

        this.lightboxIndex = (this.lightboxIndex + 1) % this.lightboxItems.length;
        this.renderLightboxImage();
      }

      onLightboxKeydown(event) {
        if (!this.lightboxNode || this.lightboxNode.hidden) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeLightbox();
          return;
        }

        if (this.lightboxImageLoading) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showPreviousLightboxImage();
          return;
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showNextLightboxImage();
        }
      }

      isLightboxZoomed() {
        return Boolean(this.lightboxNode?.classList.contains('is-zoomed'));
      }

      toggleLightboxZoom() {
        if (!this.lightboxNode || this.lightboxNode.hidden) return;

        if (this.isLightboxZoomed()) {
          this.disableLightboxZoom();
          return;
        }

        this.enableLightboxZoom();
      }

      enableLightboxZoom() {
        if (!this.lightboxNode || this.lightboxNode.hidden) return;

        this.lightboxZoom.x = 0;
        this.lightboxZoom.y = 0;
        this.lightboxZoom.isPanning = false;
        this.lightboxZoom.pointerId = null;

        this.lightboxNode.classList.add('is-zoomed');
        this.lightboxNode.classList.remove('is-panning');
        this.applyLightboxZoomTransform();
        this.updateZoomButton();
      }

      disableLightboxZoom() {
        if (!this.lightboxNode) return;

        this.cancelLightboxMouseMovePan();

        this.lightboxZoom = {
          x: 0,
          y: 0,
          startX: 0,
          startY: 0,
          originX: 0,
          originY: 0,
          isPanning: false,
          pointerId: null,
        };

        this.lightboxNode.classList.remove('is-zoomed', 'is-panning');
        this.applyLightboxZoomTransform();
        this.updateZoomButton();
      }

      applyLightboxZoomTransform() {
        if (!this.lightboxNode) return;

        this.lightboxNode.style.setProperty('--c2b-lightbox-zoom-x', `${this.lightboxZoom.x}px`);
        this.lightboxNode.style.setProperty('--c2b-lightbox-zoom-y', `${this.lightboxZoom.y}px`);
      }

      updateZoomButton() {
        const zoomButton = this.lightboxNode?.querySelector('[data-c2b-lightbox-zoom]');
        if (!zoomButton) return;

        const isZoomed = this.isLightboxZoomed();
        zoomButton.classList.toggle('is-active', isZoomed);
        zoomButton.setAttribute('aria-label', isZoomed ? 'Revino la imagine normală' : 'Mărește imaginea');
      }

      isFullscreenSupported() {
        return Boolean(
          this.lightboxNode &&
            document.fullscreenEnabled &&
            typeof this.lightboxNode.requestFullscreen === 'function' &&
            typeof document.exitFullscreen === 'function'
        );
      }

      isLightboxFullscreen() {
        return document.fullscreenElement === this.lightboxNode || document.webkitFullscreenElement === this.lightboxNode;
      }

      toggleLightboxFullscreen() {
        if (!this.isFullscreenSupported()) return;

        if (this.isLightboxFullscreen()) {
          document.exitFullscreen().catch((error) => console.warn('C2B lightbox fullscreen exit failed', error));
          return;
        }

        this.lightboxNode
          .requestFullscreen({ navigationUI: 'hide' })
          .catch((error) => console.warn('C2B lightbox fullscreen request failed', error));
      }

      onLightboxFullscreenChange() {
        this.updateFullscreenButton();
      }

      updateFullscreenButton() {
        const fullscreenButton = this.lightboxNode?.querySelector('[data-c2b-lightbox-fullscreen]');
        if (!fullscreenButton) return;

        if (!this.isFullscreenSupported()) {
          fullscreenButton.hidden = true;
          fullscreenButton.setAttribute('aria-hidden', 'true');
          return;
        }

        fullscreenButton.hidden = false;
        fullscreenButton.removeAttribute('aria-hidden');

        const isFullscreen = this.isLightboxFullscreen();
        fullscreenButton.classList.toggle('is-active', isFullscreen);
        fullscreenButton.setAttribute(
          'aria-label',
          isFullscreen ? 'Ieși din modul ecran complet' : 'Deschide pe tot ecranul'
        );
      }

      closeLightbox({ restoreFocus = true } = {}) {
        if (!this.lightboxNode) return;

        this.cancelAdjacentLightboxPreload();
        this.lightboxNode.hidden = true;
        this.lightboxNode.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('c2b-product-lightbox-open');
        document.removeEventListener('keydown', this.onLightboxKeydown);
        document.removeEventListener('fullscreenchange', this.onLightboxFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', this.onLightboxFullscreenChange);

        if (this.isLightboxFullscreen()) {
          document.exitFullscreen().catch((error) => console.warn('C2B lightbox fullscreen exit failed', error));
        }

        this.disableLightboxZoom();
        this.resetLightboxSwipe();

        if (restoreFocus && this.lightboxTrigger instanceof HTMLElement) {
          this.lightboxTrigger.focus();
        }

        this.lightboxTrigger = null;
      }
    }
  );
}
